/**
 * Organization → Software Downloads: table of downloadable customer software.
 * Currently Smart Agent; designed for additional products later.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { authedGet } from "../api/customerAuthedFetch";
import { useCustomerRoles } from "../CustomerRolesContext";
import { ROLE_UPLOADER } from "../customerRoles";
import adminStyles from "../ui/AdminStyles";
import InfoTip from "../components/InfoTip";

const TIPS = {
  software: "Product name and a short description of what the package does.",
  platform: "Operating system this build targets.",
  version: "Promoted release version available to your organization.",
  published: "When this build was published for customer download.",
  sha256: "Checksum of the installer. Verify after download if your security policy requires it.",
  download:
    "Fetches a short-lived download link for the current promoted build and starts the download.",
};

function formatPublishedAt(raw) {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  try {
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return raw;
  }
}

async function downloadSmartAgent() {
  const data = await authedGet("/customer/downloads/smart-agent");
  if (!data?.download_url) {
    throw new Error("Download URL is unavailable");
  }
  const a = document.createElement("a");
  a.href = data.download_url;
  a.rel = "noopener";
  a.download = data.filename || "AIxMedSmartAgent.exe";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return data;
}

export default function CustomerSoftwareDownloads() {
  const { hasAnyRole } = useCustomerRoles();
  const hasUploader = hasAnyRole([ROLE_UPLOADER]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [smartAgent, setSmartAgent] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authedGet("/customer/downloads/smart-agent");
      setSmartAgent(data);
    } catch (e) {
      setSmartAgent(null);
      setError(e.message || "Failed to load available software");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    document.title = "Organization · Software Downloads";
  }, []);

  async function handleDownload(productId) {
    setDownloadingId(productId);
    setError(null);
    try {
      if (productId === "smart-agent") {
        const data = await downloadSmartAgent();
        setSmartAgent(data);
      }
    } catch (e) {
      setError(e.message || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  }

  const rows = [];
  if (smartAgent) {
    rows.push({
      id: "smart-agent",
      name: "Smart Agent",
      description:
        "Windows folder-watch uploader for whole-slide images (same pipeline as browser upload).",
      platform: smartAgent.platform || "Windows",
      version: smartAgent.version,
      publishedAt: smartAgent.published_at,
      sha256: smartAgent.sha256,
      notes: smartAgent.notes,
      filename: smartAgent.filename,
    });
  }

  return (
    <div style={styles.page}>
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>Software Downloads</h2>
      <p style={styles.lede}>
        Download the currently supported builds for your organization. Packages
        listed here are the promoted releases available to customers.
      </p>

      {error ? (
        <div style={styles.error} role="alert">
          {error}
          <div style={{ marginTop: 8 }}>
            <button type="button" onClick={load}>
              Retry
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p style={{ color: "#6b7280" }}>Loading software…</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, minWidth: 280 }}>
                  <span style={styles.thTip}>
                    Software
                    <InfoTip text={TIPS.software} />
                  </span>
                </th>
                <th style={{ ...styles.th, ...styles.thCompact }}>
                  <span style={styles.thTip}>
                    Platform
                    <InfoTip text={TIPS.platform} />
                  </span>
                </th>
                <th style={{ ...styles.th, ...styles.thCompact }}>
                  <span style={styles.thTip}>
                    Version
                    <InfoTip text={TIPS.version} />
                  </span>
                </th>
                <th style={{ ...styles.th, ...styles.thCompact }}>
                  <span style={styles.thTip}>
                    Published
                    <InfoTip text={TIPS.published} />
                  </span>
                </th>
                <th style={{ ...styles.th, ...styles.thCompact }}>
                  <span style={styles.thTip}>
                    SHA-256
                    <InfoTip text={TIPS.sha256} />
                  </span>
                </th>
                <th style={{ ...styles.th, ...styles.thCompact }}>
                  <span style={styles.thTip}>
                    Download
                    <InfoTip text={TIPS.download} align="end" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td style={styles.td} colSpan={6}>
                    No software packages are published yet.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td style={{ ...styles.td, ...styles.tdSoftware }}>
                      <div style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {row.name}
                      </div>
                      <div style={styles.desc}>{row.description}</div>
                      {row.notes ? (
                        <div style={styles.notes}>{row.notes}</div>
                      ) : null}
                      {row.id === "smart-agent" ? (
                        <div style={styles.hint}>
                          Running the agent requires the{" "}
                          <strong>Uploader</strong> role
                          {hasUploader
                            ? " (your account has it)."
                            : " — ask an IT Admin if needed."}{" "}
                          Occasional uploads:{" "}
                          <Link to="/slides/upload" style={{ color: "#2563eb" }}>
                            Slides → Upload
                          </Link>
                          .
                        </div>
                      ) : null}
                    </td>
                    <td style={{ ...styles.td, ...styles.tdNowrap }}>
                      {row.platform}
                    </td>
                    <td style={{ ...styles.td, ...styles.tdNowrap }}>
                      <code>{row.version}</code>
                    </td>
                    <td style={{ ...styles.td, ...styles.tdNowrap }}>
                      {formatPublishedAt(row.publishedAt)}
                    </td>
                    <td style={{ ...styles.td, ...styles.tdNowrap }}>
                      <code style={styles.hash} title={row.sha256}>
                        {row.sha256 || "—"}
                      </code>
                    </td>
                    <td style={{ ...styles.td, ...styles.tdNowrap }}>
                      <button
                        type="button"
                        onClick={() => handleDownload(row.id)}
                        disabled={downloadingId === row.id}
                        style={styles.downloadBtn}
                      >
                        {downloadingId === row.id
                          ? "Preparing…"
                          : "Download"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: 16,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  lede: {
    fontSize: 14,
    color: "#4b5563",
    lineHeight: 1.5,
    margin: "0 0 20px",
    maxWidth: 720,
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    ...adminStyles.table,
    width: "100%",
    minWidth: 1100,
    tableLayout: "auto",
  },
  th: {
    ...adminStyles.th,
    padding: "10px 16px",
    whiteSpace: "nowrap",
  },
  thTip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  thCompact: {
    whiteSpace: "nowrap",
  },
  td: {
    ...adminStyles.td,
    padding: "12px 16px",
    verticalAlign: "top",
  },
  tdSoftware: {
    minWidth: 320,
    width: "32%",
  },
  tdNowrap: {
    whiteSpace: "nowrap",
  },
  desc: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4,
    lineHeight: 1.4,
  },
  notes: {
    fontSize: 12,
    color: "#374151",
    marginTop: 6,
    whiteSpace: "pre-wrap",
  },
  hint: {
    fontSize: 12,
    color: "#4b5563",
    marginTop: 8,
    lineHeight: 1.4,
  },
  hash: {
    fontSize: 12,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  downloadBtn: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  error: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    padding: 12,
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
};
